(() => {
  const configElement = document.getElementById('prison-newsletter-config');
  if (!configElement) return;
  let config;
  try { config = JSON.parse(configElement.textContent); } catch { return; }
  const strings = config.strings;
  const storageKey = 'prison.newsletter.checkout.v1';
  const pendingKey = storageKey + '.pending';
  const lifetime = 2 * 60 * 60 * 1000;
  const modalSelector = '[id^="newsletterModal_"]';
  const preview = config.preview_allowed && new URLSearchParams(location.search).get('preview_newsletter') === '1';
  const tracked = new WeakSet();
  const pending = new WeakMap();
  let contact = null;
  let frame = 0;
  let previewButton;

  function readStored(key) {
    try {
      const value = JSON.parse(sessionStorage.getItem(key));
      if (value && Date.now() - value.savedAt >= 0 && Date.now() - value.savedAt < lifetime && validEmail(value.email)) return value;
      sessionStorage.removeItem(key);
    } catch {}
    return null;
  }

  function storeContact(value, key = storageKey) {
    if (preview || !value || !validEmail(value.email)) return;
    const record = { email: value.email.trim(), name: (value.name || '').trim().slice(0, 120), savedAt: Date.now() };
    if (key === storageKey) contact = record;
    try { sessionStorage.setItem(key, JSON.stringify(record)); } catch {}
  }

  function validEmail(value) {
    return typeof value === 'string' && value.length <= 254 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
  }

  function capturedFields(container) {
    return {
      email: container.querySelector('input[name="email"], input[name="contact[email]"]')?.value.trim() || '',
      name: container.querySelector('input[name="name"], input[name="contact[first_name]"]')?.value.trim() || ''
    };
  }

  function node(tag, text, attribute) {
    const element = document.createElement(tag);
    if (text) element.textContent = text;
    if (attribute) element.setAttribute(attribute, '');
    return element;
  }

  function prepareCheckout(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.querySelector('input[name="contact[tags]"][value*="newsletter"]')) {
      if (form.checkValidity()) storeContact(capturedFields(form), pendingKey);
      return;
    }
    if (preview || config.customer_logged_in || event.defaultPrevented) return;
    if (event.submitter?.name !== 'checkout') return;
    const currentContact = contact || readStored(storageKey);
    if (!currentContact || Date.now() - currentContact.savedAt >= lifetime) return;
    const action = new URL(event.submitter.getAttribute('formaction') || form.action, location.origin);
    const cartPath = new URL(config.cart_url, location.origin).pathname.replace(/\/$/, '');
    if (action.origin !== location.origin || action.pathname.replace(/\/$/, '') !== cartPath) return;
    const setIfEmpty = (key, value) => {
      if (!value || action.searchParams.get(key) || form.elements.namedItem(key)?.value) return;
      action.searchParams.set(key, value);
    };
    setIfEmpty('checkout[email]', currentContact.email);
    const parts = currentContact.name.split(/\s+/).filter(Boolean);
    setIfEmpty('checkout[shipping_address][first_name]', parts.shift() || '');
    setIfEmpty('checkout[shipping_address][last_name]', parts.join(' '));
    if (event.submitter.hasAttribute('formaction')) event.submitter.setAttribute('formaction', action.href);
    else form.action = action.href;
  }

  async function cartRequest(path, body) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    try {
      const response = await fetch(config.root + path, {
        method: body ? 'POST' : 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
        ...(body ? { body: JSON.stringify(body) } : {}),
        signal: controller.signal
      });
      if (!response.ok) throw new Error('Cart request failed');
      return await response.json();
    } finally { clearTimeout(timeout); }
  }

  function discountEntries(cart) {
    if (Array.isArray(cart.discount_codes)) return cart.discount_codes;
    const applications = [
      ...(cart.cart_level_discount_applications || []),
      ...(cart.discount_applications || []),
      ...(cart.items || []).flatMap(item => (item.line_level_discount_allocations || []).map(value => value.discount_application))
    ];
    return applications.filter(value => value?.type === 'discount_code').map(value => ({ code: value.code || value.title, applicable: true }));
  }

  async function applyCoupon(button, status) {
    if (button.disabled) return;
    if (preview) {
      status.textContent = strings.preview_applied;
      button.textContent = strings.applied;
      button.disabled = true;
      return;
    }
    const component = document.querySelector('cart-additional-features');
    if (component?.constructor.discountBusy) {
      status.textContent = strings.busy;
      return;
    }
    button.disabled = true;
    button.textContent = strings.applying;
    status.textContent = '';
    try {
      let cart;
      if (typeof component?.changeDiscountCode === 'function') {
        await component.changeDiscountCode(config.coupon);
        cart = await cartRequest('cart.js');
      } else {
        const before = await cartRequest('cart.js');
        const codes = discountEntries(before).map(value => value.code).filter(Boolean);
        if (!codes.some(code => code.toUpperCase() === config.coupon.toUpperCase())) codes.push(config.coupon);
        cart = await cartRequest('cart/update.js', { discount: codes.join(',') });
        const drawer = document.querySelector('cart-drawer');
        drawer?.refreshCartDrawer?.();
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'prison-newsletter' });
      }
      const entry = discountEntries(cart).find(value => value.code?.toUpperCase() === config.coupon.toUpperCase());
      if (entry?.applicable === true) {
        status.textContent = strings.applied_message;
        button.textContent = strings.applied;
      } else if (cart.item_count === 0 && !entry) {
        const redirect = location.pathname + location.search;
        location.assign(config.root + 'discount/' + encodeURIComponent(config.coupon) + '?redirect=' + encodeURIComponent(redirect));
      } else if (entry) {
        status.textContent = strings.saved_message;
        button.textContent = strings.saved;
      } else {
        throw new Error('Discount not confirmed');
      }
    } catch {
      status.textContent = strings.error;
      button.textContent = strings.apply;
      button.disabled = false;
    }
  }

  function showSuccess(modal, button) {
    if (button.hasAttribute('data-ps-welcome-apply')) return;
    storeContact(pending.get(modal));
    pending.delete(modal);
    const content = modal.querySelector('[data-prison-welcome-content], [class^="form-container-"]');
    if (!content) return;
    button.onclick = null;
    button.type = 'button';
    button.setAttribute('data-ps-welcome-apply', '');
    button.textContent = strings.apply;
    const heading = node('h2', strings.success_title);
    heading.id = 'prison-welcome-title-' + modal.id;
    modal.setAttribute('aria-labelledby', heading.id);
    const code = node('p', config.coupon.toUpperCase(), 'data-ps-welcome-code');
    const conditions = node('p', strings.conditions, 'data-ps-welcome-conditions');
    const status = node('p', '', 'data-ps-welcome-status');
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    const close = node('button', strings.continue, 'data-ps-welcome-continue');
    close.type = 'button';
    close.addEventListener('click', () => modal.querySelector('[data-prison-welcome-close], [class^="btn-close-"]')?.click());
    button.addEventListener('click', () => applyCoupon(button, status));
    content.replaceChildren(heading, code, button, status, conditions, close);
    button.focus({ preventScroll: true });
  }

  function showPreviewSuccess(modal) {
    const content = modal?.querySelector('[data-prison-welcome-content], [class^="form-container-"]');
    if (!content) return;
    const button = node('button');
    button.className = 'btn-go';
    showSuccess(modal, button);
  }

  function enhance(modal) {
    if (!tracked.has(modal)) {
      const heading = modal.querySelector('h2');
      if (!heading || !/10\s*(?:off|%)/i.test(heading.textContent) || !/compra/i.test(heading.textContent)) return;
      tracked.add(modal);
      modal.setAttribute('data-ps-newsletter', '');
      heading.textContent = strings.title;
      heading.after(node('p', strings.benefit, 'data-ps-welcome-benefit'));
      const submit = modal.querySelector('.btn-send');
      if (submit) {
        submit.textContent = strings.release;
        if (preview) submit.disabled = false;
      }
      modal.querySelector('input[name="email"]')?.setAttribute('autocomplete', 'email');
      modal.querySelector('input[name="email"]')?.setAttribute('inputmode', 'email');
      modal.querySelector('input[name="name"]')?.setAttribute('autocomplete', 'name');
      modal.querySelector('input[name="phone"]')?.setAttribute('autocomplete', 'tel-national');
      modal.querySelector('input[name="phone"]')?.setAttribute('inputmode', 'tel');
      modal.addEventListener('click', event => {
        if (!event.target.closest?.('.btn-send')) return;
        const value = capturedFields(modal);
        if (validEmail(value.email)) pending.set(modal, value);
        if (preview) {
          event.preventDefault();
          event.stopImmediatePropagation();
          if (validEmail(value.email)) showPreviewSuccess(modal);
          else modal.querySelector('input[name="email"]')?.focus();
        }
      }, true);
      if (previewButton) {
        previewButton.disabled = false;
        previewButton.onclick = () => showPreviewSuccess(modal);
      }
    }
    const button = modal.querySelector('.btn-go');
    if (button) showSuccess(modal, button);
  }

  function scan() {
    frame = 0;
    document.querySelectorAll(modalSelector).forEach(enhance);
  }

  function start() {
    contact = readStored(storageKey);
    if (!preview && new URLSearchParams(location.search).get('contact_posted') === 'true') {
      storeContact(readStored(pendingKey));
      try { sessionStorage.removeItem(pendingKey); } catch {}
    }
    if (preview) {
      const key = '_mn_929cfb79-511e-4575-bc64-cec44052d519';
      document.cookie = key + '=; Max-Age=0; path=/; SameSite=Lax; Secure';
      for (const storage of ['sessionStorage', 'localStorage']) {
        try { window[storage].removeItem(key); } catch {}
      }
      const notice = node('aside', '', 'data-ps-newsletter-preview');
      notice.appendChild(node('p', strings.preview_note));
      previewButton = node('button', strings.preview_coupon);
      previewButton.type = 'button';
      previewButton.disabled = true;
      notice.appendChild(previewButton);
      const reopen = node('button', strings.preview_reopen);
      reopen.type = 'button';
      reopen.addEventListener('click', () => location.reload());
      notice.appendChild(reopen);
      document.body.appendChild(notice);
    }
    document.addEventListener('submit', prepareCheckout, true);
    scan();
    new MutationObserver(() => { if (!frame) frame = requestAnimationFrame(scan); }).observe(document.body, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
